import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import useGetAgent from '@/hooks/useGetAgent';
import useGetProfile from '@/hooks/useGetProfile';
import { useAuth0 } from '@auth0/auth0-react';
import { FileText, HelpCircle, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import Faq from './Faq';
import LoadingModal from './LoadingModal';
import PropertyForm from './PropertyForm';
const HomePage = () => {
  const navigate = useNavigate();
  const [query] = useSearchParams();

  const agentIdentifier = useMemo(() => {
    const agent = query.get('agent');
    if (agent) window.localStorage.setItem('agent', agent.replace(' ', '+'));
    const localAgent = window.localStorage.getItem('agent');
    return localAgent;
  }, [query]);

  const { data: profile } = useGetProfile();
  const {
    isLoading,
    handleRedirectCallback,
    loginWithRedirect,
    isAuthenticated,
  } = useAuth0();
  const [showSponsor, setShowSponsor] = useState(false);
  const [propertyDataLoaded, setPropertyDataLoaded] = useState(false);
  const location = useLocation();
  const { data: agent } = useGetAgent(profile?.agent ? null : agentIdentifier);

  const firstName = query.get('firstName');
  const lastName = query.get('lastName');

  const _agent = useMemo(() => {
    if (profile?.agent?.firstName) return profile.agent;
    if (agent) return agent;
    return null;
  }, [agent, profile?.agent]);

  useEffect(() => {
    if (firstName && lastName && !isLoading) {
      window.localStorage.setItem(
        'user',
        JSON.stringify({ firstName, lastName }),
      );
      window.localStorage.setItem('user_type', 'USER');

      loginWithRedirect({
        authorizationParams: {
          screen_hint: 'signup',
        },
        appState: {
          returnTo: '/dashboard?tab=reports',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName, isLoading]);

  useEffect(() => {
    if (_agent) setShowSponsor(true);
  }, [_agent]);

  useEffect(() => {
    if (!isLoading) {
      (async () => {
        const callback = await handleRedirectCallback();
        if (callback?.appState?.returnTo) navigate(callback.appState.returnTo);
      })();
    }
  }, [isLoading]);

  // Handler to be passed to PropertyForm to update when data is loaded
  const handlePropertyDataLoaded = (isLoaded: boolean) => {
    setPropertyDataLoaded(isLoaded);
  };

  // Add effect to scroll to property form if hash is present
  useEffect(() => {
    if (location.hash === '#property-form') {
      const element = document.getElementById('property-form');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
          });
        }, 100);
      }
    }
  }, [location]);

  if (isLoading)
    return (
      <div>
        <LoadingModal show />
      </div>
    );

  return (
    <div className="container mx-auto px-4 py-8 relative">
      {/* Modify this area to match the current sponsor, if any. */}
      {showSponsor && (
        <div className="absolute top-0 right-0 bg-white rounded-lg shadow-md p-4 border border-[#D6BCFA] max-w-[240px]">
          <div className="flex justify-between items-start mb-2">
            <div className="text-[#6E59A5] text-sm font-medium">
              Provided Courtesy Of:
            </div>
            <button
              onClick={() => setShowSponsor(false)}
              className="text-[#8E9196] hover:text-[#333] transition-colors"
              aria-label="Close sponsor"
            >
              <X size={16} />
            </button>
          </div>

          <div className="flex flex-col items-center text-center">
            <Avatar className="h-20 w-20 border-2 border-[#D32F2F]">
              <AvatarImage
                src={_agent?.photoUrl}
                alt={_agent?.firstName || 'Agent Avatar'}
              />
              <AvatarFallback className="bg-[#D6BCFA] uppercase">
                {_agent?.firstName?.[0]}
                {_agent?.lastName?.[0]}
              </AvatarFallback>
            </Avatar>

            <div className="mt-2">
              <h3 className="font-medium text-[#1A1F2C] capitalize">
                {_agent?.firstName} {_agent?.lastName}
              </h3>
              <p className="text-sm text-[#333]">{_agent?.companyName}</p>
              {_agent?.phoneNumber && (
                <p className="text-xs text-[#8E9196] mt-1">
                  tel. {_agent?.phoneNumber}
                </p>
              )}
              <p className="text-xs text-[#8E9196]">
                <a
                  href={`mailto:${_agent?.emailAddress}`}
                  className="hover:text-[#D32F2F] transition-colors"
                >
                  {_agent?.emailAddress}
                </a>
              </p>
            </div>
          </div>
        </div>
      )}

      <section className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-bold tracking-tight mb-6">
            Protest Your Property Tax Valuation
          </h1>
          <p className="text-lg md:text-xl text-tax-neutralGray mb-8 max-w-2xl mx-auto">
            Find out if your home has been overvalued and learn how to appeal
            your county tax assessment. 100% free to use.
          </p>

          {/* Moved "How Property Tax Appeals Work" section here */}
          <div className="py-8 bg-tax-lightGray rounded-lg mb-8">
            <div className="max-w-4xl mx-auto px-4">
              <h2 className="text-3xl font-semibold text-center mb-12">
                How Property Tax Appeals Work
              </h2>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-tax-borderGray">
                  <div className="h-12 w-12 bg-[#D32F2F] rounded-full flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold text-xl">1</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-center text-tax-deepBlue">
                    Find Your Property
                  </h3>
                  <p className="text-tax-neutralGray text-center">
                    Enter your address below and we'll retrieve all the
                    pertinent data necessary to start an appeal.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border border-tax-borderGray">
                  <div className="h-12 w-12 bg-[#D32F2F] rounded-full flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold text-xl">2</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-center text-tax-deepBlue">
                    Build Your Case
                  </h3>
                  <p className="text-tax-neutralGray text-center">
                    We'll help you find comparable sales data necessary to argue
                    your case for a lower value.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border border-tax-borderGray">
                  <div className="h-12 w-12 bg-[#D32F2F] rounded-full flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold text-xl">3</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-center text-tax-deepBlue">
                    File Your Appeal
                  </h3>
                  <p className="text-tax-neutralGray text-center">
                    We'll give you a complete letter, report and instructions on
                    submitting it to your local assessor.
                  </p>
                </div>
              </div>

              {/* Removed "Learn More About The Process" button */}
            </div>
          </div>

          <div className="mb-12">
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="h-8 w-8 bg-[#D32F2F] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-md">1</span>
                </div>
                <h3 className="text-xl font-semibold text-tax-deepBlue">
                  ENTER YOUR PROPERTY ADDRESS
                </h3>
              </div>
              <PropertyForm onPropertyDataLoaded={handlePropertyDataLoaded} />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-semibold text-center mb-4">
            Why Appeal Your Property Taxes?
          </h2>
          <p className="text-lg text-center text-tax-neutralGray mb-12 max-w-2xl mx-auto">
            Many property owners are paying more than they should due to county
            overvaluations.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="border border-tax-borderGray rounded-lg p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 bg-tax-deepBlue/10 rounded-full flex items-center justify-center mr-4">
                  <FileText className="h-5 w-5 text-tax-deepBlue" />
                </div>
                <h3 className="text-xl font-semibold text-tax-deepBlue">
                  County Errors Are Common
                </h3>
              </div>
              <p className="text-tax-neutralGray">
                Mass appraisal techniques often miss important details about
                your specific property, leading to overvaluation and higher tax
                bills.
              </p>
            </div>

            <div className="border border-tax-borderGray rounded-lg p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 bg-tax-deepBlue/10 rounded-full flex items-center justify-center mr-4">
                  <HelpCircle className="h-5 w-5 text-tax-deepBlue" />
                </div>
                <h3 className="text-xl font-semibold text-tax-deepBlue">
                  Save Money Every Year
                </h3>
              </div>
              <p className="text-tax-neutralGray">
                A successful appeal can lower your property tax bill for the
                current year and potentially set a precedent for future
                assessments.
              </p>
            </div>
          </div>

          {/* Tax Appeal Guidance component has been moved to PropertyForm component */}
        </div>
      </section>

      <section className="py-12 md:py-16 bg-tax-lightGray rounded-lg">
        <div className="max-w-4xl mx-auto px-4">
          <Faq />

          <div className="mt-10 text-center">
            <Button
              asChild
              variant="outline"
              className="max-w-xs min-h-[3rem] px-6 bg-primary border-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground rounded-lg"
            >
              <Link to="/faq">View All FAQs</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
export default HomePage;
